import { motion } from "framer-motion";
import { CloudSun, EyeOff, ShieldAlert, Wrench } from "lucide-react";
import { WHATSAPP_URL, WhatsAppIcon, WHY_POINTS } from "@/lib/site";

const FadeIn = ({ children, className = "", delay = 0 }) => (
    <motion.div
        initial={{ opacity: 0, y: 24 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-80px" }}
        transition={{ duration: 0.5, delay }}
        className={className}
    >
        {children}
    </motion.div>
);

export const Problem = () => (
    <section id="problema" className="scroll-mt-20 border-b-2 border-ink bg-white">
        <div className="mx-auto grid max-w-7xl gap-10 px-4 py-20 sm:px-6 lg:grid-cols-2 lg:items-center lg:py-24">
            <FadeIn>
                <p className="mb-4 inline-block border-2 border-ink bg-cream px-3 py-1 text-xs font-bold uppercase tracking-widest">
                    Il problema
                </p>
                <h2 className="headline-display text-4xl font-bold sm:text-5xl">
                    Perché i fari diventano opachi
                </h2>
                <p className="mt-6 text-base font-medium leading-relaxed text-ink/80 md:text-lg">
                    I fari moderni sono in policarbonato: con il tempo raggi UV,
                    agenti atmosferici e lavaggi aggressivi ne degradano la
                    superficie esterna, che diventa opaca e ingiallita.
                </p>
                <p className="mt-4 text-base font-medium leading-relaxed text-ink/80 md:text-lg">
                    Oltre all'estetica, un faro deteriorato può ridurre la
                    qualità dell'illuminazione. Il risultato di un ripristino
                    dipende dalle condizioni iniziali del componente: per
                    questo ogni caso va valutato singolarmente.
                </p>
                <p className="mt-4 text-base font-medium leading-relaxed text-ink/80 md:text-lg">
                    Spesso il deterioramento riguarda solo la superficie
                    esterna del faro: in questi casi si può intervenire con un
                    trattamento mirato, senza sostituire il gruppo ottico. Se
                    invece il danno è interno o strutturale, te lo diciamo
                    subito e con chiarezza.
                </p>
            </FadeIn>
            <FadeIn delay={0.1} className="grid gap-4">
                {[
                    {
                        icon: CloudSun,
                        title: "Esposizione quotidiana",
                        text: "Sole, pioggia e lavaggi consumano la protezione originale del policarbonato.",
                    },
                    {
                        icon: EyeOff,
                        title: "Luce meno efficace",
                        text: "La superficie opaca diffonde il fascio luminoso invece di proiettarlo correttamente.",
                    },
                    {
                        icon: ShieldAlert,
                        title: "Estetica e controlli",
                        text: "Fari compromessi rovinano il frontale dell'auto e possono creare problemi in fase di revisione.",
                    },
                ].map(({ icon: Icon, title, text }) => (
                    <div
                        key={title}
                        className="flex gap-4 border-2 border-ink bg-cream p-5"
                    >
                        <Icon className="mt-1 h-6 w-6 shrink-0 text-royal" aria-hidden="true" />
                        <div>
                            <h3 className="font-display text-lg font-semibold uppercase tracking-tight">
                                {title}
                            </h3>
                            <p className="mt-1 text-sm font-medium leading-relaxed text-ink/75">
                                {text}
                            </p>
                        </div>
                    </div>
                ))}
            </FadeIn>
        </div>
    </section>
);

export const Service = () => (
    <section id="servizio" className="scroll-mt-20 border-b-2 border-ink bg-cream">
        <div className="mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:py-24">
            <FadeIn className="max-w-3xl">
                <p className="mb-4 inline-block border-2 border-ink bg-white px-3 py-1 text-xs font-bold uppercase tracking-widest">
                    Il servizio
                </p>
                <h2 className="headline-display text-4xl font-bold sm:text-5xl">
                    Ripristino dei fari, dove si trova la tua auto
                </h2>
                <p className="mt-6 text-base font-medium leading-relaxed text-ink/80 md:text-lg">
                    Interveniamo a domicilio: a casa tua o dove è parcheggiata
                    l'auto. Ci contatti su WhatsApp, ci mandi alcune foto dei
                    fari e ti diciamo con chiarezza se e come possiamo
                    intervenire. Il trattamento lavora sulla superficie esterna
                    del faro: pulizia, carteggiatura e lucidatura secondo il
                    livello di opacità, con protezione nanotecnologica finale.
                </p>
                <p className="mt-4 text-base font-medium leading-relaxed text-ink/80 md:text-lg">
                    Prima di fissare l'appuntamento valutiamo le fotografie e
                    ti confermiamo pacchetto, prezzo e tempistiche: nessuna
                    sorpresa il giorno dell'intervento. Se il faro non è
                    recuperabile, te lo diciamo prima.
                </p>
            </FadeIn>
            <FadeIn delay={0.1} className="mt-12">
                <figure className="border-2 border-ink bg-ink shadow-print">
                    <img
                        src="/assets/lavori/lavorazione-nera.jpg"
                        alt="Lavorazione CLEAR LENS MILANO a domicilio: faro mascherato con nastro di protezione prima del trattamento"
                        loading="lazy"
                        className="h-72 w-full object-cover sm:h-96"
                    />
                    <figcaption className="flex items-center justify-between bg-ink px-4 py-2 text-xs font-bold uppercase tracking-widest text-cream">
                        <span>Lavorazione a domicilio</span>
                        <span className="text-wa">Mascheratura e protezione</span>
                    </figcaption>
                </figure>
            </FadeIn>
            <div className="mt-12 grid gap-6 md:grid-cols-3">
                {[
                    {
                        n: "01",
                        title: "Pulizia profonda",
                        text: "Preparazione della superficie per rimuovere sporco e contaminanti dal policarbonato.",
                    },
                    {
                        n: "02",
                        title: "Carteggiatura e lucidatura",
                        text: "Rimozione dell'opacità e dell'ossidazione con lucidatura tecnica o multistadio, in base al caso.",
                    },
                    {
                        n: "03",
                        title: "Protezione nanotecnologica",
                        text: "Finitura con CarPro C.Quartz per proteggere il risultato nel tempo.",
                    },
                ].map((s, i) => (
                    <FadeIn key={s.n} delay={i * 0.08}>
                        <div className="h-full border-2 border-ink bg-white p-6 transition-[transform,box-shadow] duration-150 hover:-translate-y-1 hover:shadow-print">
                            <span className="font-display text-4xl font-bold text-royal">
                                {s.n}
                            </span>
                            <h3 className="mt-3 font-display text-xl font-semibold uppercase tracking-tight">
                                {s.title}
                            </h3>
                            <p className="mt-2 text-sm font-medium leading-relaxed text-ink/75">
                                {s.text}
                            </p>
                        </div>
                    </FadeIn>
                ))}
            </div>
            <FadeIn delay={0.15} className="mt-10">
                <a
                    href={WHATSAPP_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="service-whatsapp-btn"
                    className="inline-flex items-center gap-2 border-2 border-ink bg-wa px-6 py-3 font-display text-lg font-semibold uppercase tracking-tight text-ink transition-[transform,box-shadow] duration-150 hover:-translate-y-1 hover:shadow-print"
                >
                    <WhatsAppIcon className="h-5 w-5" />
                    Richiedi una valutazione
                </a>
            </FadeIn>
        </div>
    </section>
);

export const WhyUs = () => (
    <section id="perche-noi" className="scroll-mt-20 border-b-2 border-ink bg-navy text-cream navy-lines">
        <div className="mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:py-24">
            <FadeIn className="max-w-3xl">
                <p className="mb-4 inline-block border-2 border-cream bg-navy-deep px-3 py-1 text-xs font-bold uppercase tracking-widest">
                    Perché Clear Lens Milano
                </p>
                <h2 className="headline-display text-4xl font-bold sm:text-5xl">
                    Artigianale, diretto, senza perdite di tempo
                </h2>
                <p className="mt-6 text-base font-medium leading-relaxed text-cream/80 md:text-lg">
                    Niente call center e niente attese in officina: parli
                    direttamente con chi esegue il lavoro, su un'auto alla
                    volta. Ogni faro viene valutato per le sue condizioni reali
                    e trattato con la cura di chi fa questo mestiere in modo
                    artigianale.
                </p>
            </FadeIn>
            <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
                {WHY_POINTS.map((p, i) => (
                    <FadeIn key={p.title} delay={(i % 3) * 0.08}>
                        <div className="h-full border-2 border-cream/90 bg-navy-deep p-6">
                            <Wrench className="h-6 w-6 text-wa" aria-hidden="true" />
                            <h3 className="mt-3 font-display text-xl font-semibold uppercase tracking-tight">
                                {p.title}
                            </h3>
                            <p className="mt-2 text-sm font-medium leading-relaxed text-cream/80">
                                {p.text}
                            </p>
                        </div>
                    </FadeIn>
                ))}
            </div>
        </div>
    </section>
);
